import { motion, useAnimation, useScroll, useTransform } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, ArrowDown, Star, Award, Users, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showArabicText, setShowArabicText] = useState(false);
  const [arabicAnimationPhase, setArabicAnimationPhase] = useState(0);
  const controls = useAnimation();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const CLOUDINARY_BASE = "https://res.cloudinary.com/dpk0ydobt/image/upload/q_auto,f_auto/placof/";

  const heroImages = [
    {
      url: `${CLOUDINARY_BASE}projets/Salle des fêtes Layelina.jpg`,
      title: 'Salle des fêtes Layelina',
      description: 'Design contemporain avec des matériaux nobles'
    },
    {
      url: `${CLOUDINARY_BASE}projets/Salle des fêtes Layeli.jpg`,
      title: 'Salle des fêtes Layeli Palace',
      description: 'Fonctionnalité et esthétique parfaitement combinées'
    },
    {
      url: `${CLOUDINARY_BASE}projets/Café Le Saphir Bleu.jpg`,
      title: 'Café Le Saphir Bleu',
      description: 'Un havre de paix au design sophistiqué'
    },
    {
      url: `${CLOUDINARY_BASE}projets/Salle des fêtes Cité El Ons.png`,
      title: 'Salle des fêtes Cité El Ons',
      description: 'Un havre de paix au design sophistiqué'
    },
  ];

  const stats = [
    { icon: Award, number: '200+', label: 'Projets Réalisés' },
    { icon: Users, number: '500+', label: 'Clients Satisfaits' },
    { icon: Star, number: '4.8/5', label: 'Note Moyenne' }
  ];

  // Animation phases pour le texte arabe
  useEffect(() => {
    controls.start('visible');

    const arabicSequence = async () => {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setShowArabicText(true);

      // Phase 1: Apparition
      setArabicAnimationPhase(1);
      await new Promise(resolve => setTimeout(resolve, 5000));

      // Phase 2: Effet de brillance
      setArabicAnimationPhase(2);
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Phase 3: Animation continue
      setArabicAnimationPhase(3);

      // Disparition après 10 secondes au total
      await new Promise(resolve => setTimeout(resolve, 10000)); // 1200+2000+1500+5300 = 10000ms
      setShowArabicText(false);
    };

    arabicSequence();
  }, [controls]);

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying, heroImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 60, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100
      }
    }
  };

  const floatingVariants = {
    float: {
      y: [-8, 8],
      rotate: [-2, 2],
      transition: {
        y: {
          repeat: Infinity,
          repeatType: 'reverse',
          duration: 4,
          ease: 'easeInOut'
        },
        rotate: {
          repeat: Infinity,
          repeatType: 'reverse',
          duration: 6,
          ease: 'easeInOut'
        }
      }
    }
  };

  // Animations sophistiquées pour le texte arabe
  const arabicContainerVariants = {
    hidden: {
      y: -300,
      opacity: 0,
      scale: 0.3,
      rotate: -20,
      filter: 'blur(10px)'
    },
    phase1: {
      y: 0,
      opacity: 1,
      scale: 1.4,
      rotate: 0,
      filter: 'blur(0px)',
      transition: {
        type: 'spring',
        damping: 8,
        stiffness: 80,
        duration: 2
      }
    },
    phase2: {
      scale: [1.1, 1.2, 1.1],
      rotate: [0, 5, -5, 0],
      filter: [
        'blur(0px) hue-rotate(0deg) brightness(1)',
        'blur(1px) hue-rotate(180deg) brightness(1.3)',
        'blur(0px) hue-rotate(0deg) brightness(1)'
      ],
      transition: {
        duration: 1.5,
        ease: 'easeInOut'
      }
    },
    phase3: {
      y: [0, -8, 0],
      rotate: [0, 2, -2, 0],
      scale: [1, 1.02, 1],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    },
    hover: {
      scale: 1.08,
      rotate: 3,
      y: -5,
      filter: 'brightness(1.2) saturate(1.3)',
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    }
  };

  const arabicTextVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      filter: 'blur(5px)'
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 1,
        delay: 0.5,
        ease: 'easeOut'
      }
    }
  };

  const getArabicAnimationState = () => {
    switch (arabicAnimationPhase) {
      case 1: return 'phase1';
      case 2: return 'phase2';
      case 3: return 'phase3';
      default: return 'hidden';
    }
  };

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center overflow-hidden 
                pt-[70px] md:pt-[70px]
                pb-24 sm:pb-32 md:pb-40 lg:pb-0"
    >
      {/* Image Carousel Background */}
      <motion.div
        className="absolute inset-0"
        style={{ y: backgroundY }}
      >
        {heroImages.map((image, index) => (
          <motion.div
            key={index}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{
              opacity: index === currentSlide ? 1 : 0,
              scale: index === currentSlide ? 1 : 1.1
            }}
            transition={{ duration: 1.5, ease: 'easeInOut' }}
            style={{
              backgroundImage: `url(${image.url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </motion.div>

      {/* Parallax Background Elements */}
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-0"
      >
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-gradient-to-r from-amber-400/20 to-orange-500/20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 60 + 30}px`,
              height: `${Math.random() * 60 + 30}px`,
            }}
            animate={{
              y: [0, (Math.random() - 0.5) * 150],
              x: [0, (Math.random() - 0.5) * 80],
              rotate: [0, 360],
              opacity: [0.1, 0.4, 0.1],
              scale: [1, 1.3, 1]
            }}
            transition={{
              duration: Math.random() * 25 + 20,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'linear'
            }}
          />
        ))}

        <motion.div
          className="absolute -top-32 -right-32 w-96 h-96 bg-gradient-to-br from-amber-400/20 to-orange-600/20 rounded-full backdrop-blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: 'linear'
          }}
        />

        <motion.div
          className="absolute -bottom-32 -left-32 w-80 h-80 bg-gradient-to-tr from-blue-400/20 to-purple-600/20 rounded-full backdrop-blur-3xl"
          animate={{
            scale: [1.3, 1, 1.3],
            rotate: [360, 180, 0],
            opacity: [0.4, 0.7, 0.4]
          }}
          transition={{
            duration: 35,
            repeat: Infinity,
            ease: 'linear'
          }}
        />
      </motion.div>

      {/* Texte Arabe Animé Sophistiqué */}
      {showArabicText && (
        <motion.div
          className="absolute left-1/2 transform -translate-x-1/2 z-30 px-4
                    top-[10%]   // Position par défaut (écrans très petits)
                    sm:top-[15%]  // Petits écrans (>640px)
                    md:top-[8%]  // Tablettes (>768px)
                    lg:top-[8%]   // Desktop (>1024px)
                    xl:top-[10%]  // Grands écrans (>1280px)"
          initial="hidden"
          animate={getArabicAnimationState()}
          whileHover="hover"
          variants={arabicContainerVariants}
        >
          {/* Aura lumineuse autour du conteneur */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-amber-400/30 to-orange-500/30 rounded-lg md:rounded-xl blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />

          {/* Conteneur principal avec effet glassmorphism avancé */}
          <div className="relative bg-gradient-to-r from-orange-500/95 to-amber-500/95 backdrop-blur-lg rounded-lg md:rounded-xl px-4 md:px-8 py-2 md:py-4 border-2 border-white/30 shadow-2xl mt-7 md:mt-8 overflow-hidden">

            {/* Effet de brillance animé */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
              animate={{
                x: ['-100%', '200%']
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                repeatDelay: 4,
                ease: 'easeInOut'
              }}
            />

            {/* Particules flottantes à l'intérieur */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white/60 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`
                }}
                animate={{
                  y: [0, -20, 0],
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0]
                }}
                transition={{
                  duration: Math.random() * 3 + 2,
                  delay: Math.random() * 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
              />
            ))}

            <div className="relative text-center">
              {/* Icône décorative */}
              <motion.div
                className="flex justify-center mb-1 md:mb-2"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 3, duration: 1, ease: 'easeOut' }}
              >
                <Sparkles className="w-4 h-4 md:w-6 md:h-6 text-white/80" />
              </motion.div>

              {/* Texte arabe avec animation de lettres */}
              <motion.p
                className="text-white text-sm md:text-xl lg:text-2xl font-bold mb-1 md:mb-2"
                style={{ fontFamily: 'Arial, sans-serif', direction: 'rtl' }}
                variants={arabicTextVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.span
                  className="inline-block"
                  animate={{
                    textShadow: [
                      '0 0 5px rgba(255,255,255,0.5)',
                      '0 0 20px rgba(255,255,255,0.8)',
                      '0 0 5px rgba(255,255,255,0.5)'
                    ]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                >
                  انتي عليك تبني و احنا علينا الديكور
                </motion.span>
              </motion.p>

              {/* Séparateur animé */}
              <motion.div
                className="flex items-center justify-center gap-2 mb-1"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 'auto', opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.8 }}
              >
                <motion.div
                  className="h-px bg-gradient-to-r from-transparent via-white/60 to-transparent flex-1"
                  animate={{ scaleX: [0, 1, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <Star className="w-3 h-3 md:w-4 md:h-4 text-white/80" />
                <motion.div
                  className="h-px bg-gradient-to-r from-transparent via-white/60 to-transparent flex-1"
                  animate={{ scaleX: [0, 1, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
                />
              </motion.div>
            </div>
          </div>

          {/* Particules magiques sortant du conteneur */}
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 md:w-3 md:h-3"
              style={{
                left: `${Math.random() * 100}%`,
                top: '100%'
              }}
              animate={{
                y: [0, Math.random() * -300 - 100],
                x: [(Math.random() - 0.5) * 100],
                opacity: [1, 0.8, 0],
                scale: [1, 0.5, 0],
                rotate: [0, Math.random() * 720]
              }}
              transition={{
                duration: Math.random() * 4 + 3,
                delay: Math.random() * 3,
                repeat: Infinity,
                repeatDelay: Math.random() * 8 + 5,
                ease: 'easeOut'
              }}
            >
              <div className="w-full h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full shadow-lg" />
              <motion.div
                className="absolute inset-0 bg-white/50 rounded-full"
                animate={{
                  scale: [0, 1.5, 0],
                  opacity: [1, 0]
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: 'easeOut'
                }}
              />
            </motion.div>
          ))}

          {/* Rayons lumineux */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={`ray-${i}`}
              className="absolute top-1/2 left-1/2 origin-bottom"
              style={{
                width: '2px',
                height: '60px',
                background: 'linear-gradient(to top, rgba(255,255,255,0.8), transparent)',
                transform: `rotate(${i * 60}deg) translateX(-50%) translateY(-50%)`
              }}
              animate={{
                opacity: [0, 0.8, 0],
                scaleY: [0.5, 1.2, 0.5]
              }}
              transition={{
                duration: 2,
                delay: i * 0.3,
                repeat: Infinity,
                repeatDelay: 3,
                ease: 'easeInOut'
              }}
            />
          ))}
        </motion.div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            animate={controls}
            variants={containerVariants}
            className="space-y-4 lg:space-y-6 text-center lg:text-left"
          >
            <motion.div variants={itemVariants}>
              <motion.div
                variants={floatingVariants}
                animate="float"
                className="inline-block"
              >
                <span className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm font-semibold tracking-wider text-white uppercase bg-gradient-to-r from-amber-500/20 to-orange-600/20 rounded-full backdrop-blur-sm border border-white/20">
                  <Star className="w-3 h-3 md:w-4 md:h-4" />
                  Nouvelle Collection {new Date().getFullYear()}
                </span>
              </motion.div>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black mb-4 lg:mb-6 leading-tight"
            >
              <span className="block text-white drop-shadow-2xl">
                Transformez
              </span>
              <span className="block bg-gradient-to-r from-amber-400 to-orange-600 bg-clip-text text-transparent">
                votre espace
              </span>
              <span className="block text-white drop-shadow-2xl">
                avec élégance
              </span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 leading-relaxed font-light px-2 lg:px-0"
            >
              Spécialiste de l'isolation, placo plâtre, enduit de plâtre et plafond démontable en Tunisie.

              <br />
              Des solutions de décoration intérieure et extérieure sur mesure pour créer des espaces exceptionnels qui reflètent votre personnalité.
            </motion.p>

            {/* Action Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start"
            >
              <motion.button
                className="group relative overflow-hidden px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-full font-bold text-white bg-gradient-to-r from-amber-500 to-orange-600 shadow-2xl text-xs sm:text-sm md:text-base"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600"
                  initial={{ x: '100%' }}
                  whileHover={{ x: '0%' }}
                  transition={{ duration: 0.3 }}
                />
                <a href="tel:+21697286007"><span className="relative z-10">Prendre contact avec nous</span></a>
              </motion.button>

              <Link to="/projects">
                <motion.button
                  className="group px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-full font-bold text-white bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white hover:text-gray-900 transition-all duration-300 text-xs sm:text-sm md:text-base"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Découvrir nos réalisations
                </motion.button>
              </Link>
            </motion.div>

            {/* Stats */}
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6 pt-4 lg:pt-8 max-w-xs sm:max-w-md mx-auto lg:mx-0"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="flex justify-center mb-1 sm:mb-2">
                    <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-8 lg:h-8 text-amber-400" />
                  </div>
                  <div className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-white">{stat.number}</div>
                  <div className="text-xs sm:text-sm text-white/70">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Image Showcase */}
          <motion.div
            variants={itemVariants}
            className="relative mt-2 lg:mt-0 px-2 lg:px-0"
          >
            <div className="relative">
              {/* Main Image Display */}
              <motion.div
                className="relative h-60 sm:h-64 md:h-80 lg:h-[400px] xl:h-[500px] rounded-xl lg:rounded-2xl xl:rounded-3xl overflow-hidden shadow-2xl"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                style={{
                  marginBottom: '40px' // Ajoute de l'espace pour les indicateurs
                }}
              >
                <img
                  src={heroImages[currentSlide].url}
                  alt={heroImages[currentSlide].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                {/* Image Info */}
                <div className="absolute bottom-2 sm:bottom-4 lg:bottom-6 left-2 sm:left-4 lg:left-6 text-white">
                  <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold mb-1">{heroImages[currentSlide].title}</h3>
                  <p className="text-xs sm:text-sm text-white/80 hidden sm:block">{heroImages[currentSlide].description}</p>
                </div>
              </motion.div>

              {/* Navigation Controls */}
              <div className="absolute top-1/2 -translate-y-1/2 left-1 sm:left-2 lg:left-4">
                <motion.button
                  onClick={prevSlide}
                  className="p-1.5 sm:p-2 lg:p-3 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-all"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                </motion.button>
              </div>

              <div className="absolute top-1/2 -translate-y-1/2 right-1 sm:right-2 lg:right-4">
                <motion.button
                  onClick={nextSlide}
                  className="p-1.5 sm:p-2 lg:p-3 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-all"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6" />
                </motion.button>
              </div>

              {/* Play/Pause Control */}
              <div className="absolute top-1 sm:top-2 lg:top-4 right-1 sm:right-2 lg:right-4">
                <motion.button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="p-1.5 sm:p-2 lg:p-3 rounded-full bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 transition-all"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  {isPlaying ? <Pause className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" /> : <Play className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />}
                </motion.button>
              </div>

              {/* Dots Indicator */}
              <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 sm:gap-2">
                {heroImages.map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all ${index === currentSlide
                      ? 'bg-amber-400 w-6 sm:w-8'
                      : 'bg-white/30 hover:bg-white/50'
                      }`}
                    whileHover={{ scale: 1.2 }}
                  />
                ))}
              </div>
            </div>

            {/* Floating Cards */}
            <motion.div
              className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 lg:-top-6 lg:-left-6 p-2 sm:p-3 lg:p-4 bg-white/10 backdrop-blur-md rounded-lg sm:rounded-xl lg:rounded-2xl border border-white/20"
              animate={{ y: [-3, 3, -3] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="text-amber-400 font-bold text-lg sm:text-xl lg:text-2xl">13+</div>
              <div className="text-white text-xs sm:text-sm">Années d'expérience</div>
            </motion.div>

            <motion.div
              className="absolute -bottom-8 -right-3 sm:-bottom-10 sm:-right-4 lg:-bottom-6 lg:-right-6 p-2 sm:p-3 lg:p-4 bg-white/10 backdrop-blur-md rounded-lg sm:rounded-xl lg:rounded-2xl border border-white/20"
              animate={{ y: [3, -3, 3] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="text-orange-400 font-bold text-lg sm:text-xl lg:text-2xl">100%</div>
              <div className="text-white text-xs sm:text-sm">Satisfaction client</div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-4 sm:bottom-6 lg:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: [0, 1, 0],
          y: [20, 0, -10]
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      >
        <span className="text-xs sm:text-sm text-white/80 mb-2 sm:mb-3 font-medium">Découvrir plus</span>
        <motion.div
          className="flex flex-col items-center"
          whileHover={{ scale: 1.1 }}
        >
          <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/50 rounded-full flex justify-center backdrop-blur-sm">
            <motion.div
              className="w-1 h-2 sm:w-1.5 sm:h-3 bg-white rounded-full mt-1.5 sm:mt-2"
              animate={{
                y: [0, 6, 0],
                opacity: [1, 0.3, 1]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            />
          </div>
          <ArrowDown className="w-3 h-3 sm:w-4 sm:h-4 text-white/50 mt-1 sm:mt-2" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;